'use client';

import { useRef } from 'react';
import { gsap, EASE_OUT } from '@/components/sections/kit/motion';
import type { CompareDossierData } from './types';
import { CONTAINER, ColumnPill, Stamp } from './bits';
import DossierHead from './DossierHead';
import { stampIn, useChoreo } from './motion';

/**
 * §05 — cost of scale. Left: the competitor's mono ledger, price ÷ published
 * monthly quota, counting up to the real figures (honesty rail: if Bitly
 * reprices, recompute or pull the section — never leave stale math). Right:
 * "No denominator." — there is no per-link price to compute here.
 */
export default function CostOfScale({ data }: { data: CompareDossierData }) {
  const rootRef = useRef<HTMLElement>(null);

  useChoreo(rootRef, (root) => {
    const tl = gsap.timeline({ paused: true, defaults: { ease: EASE_OUT } });
    tl.from(root.querySelectorAll('[data-cost-col]'), {
      y: 24,
      opacity: 0,
      duration: 0.7,
      stagger: 0.14,
    });
    // Count each ledger figure up from zero to its published value, keeping
    // the raw string's shape: leading symbol ($ or none), decimal places,
    // thousands separators ("3,500").
    root.querySelectorAll<HTMLElement>('[data-count]').forEach((el, i) => {
      const raw = el.dataset.count ?? '';
      const firstDigit = raw.search(/[0-9]/);
      if (firstDigit < 0) return;
      const prefix = raw.slice(0, firstDigit);
      const grouped = raw.includes(',');
      const target = Number.parseFloat(raw.replace(/[^0-9.]/g, ''));
      const decimals = raw.split('.')[1]?.length ?? 0;
      if (!Number.isFinite(target)) return;
      const proxy = { v: 0 };
      tl.to(
        proxy,
        {
          v: target,
          duration: 1.1,
          ease: 'power2.out',
          onUpdate: () => {
            el.textContent = grouped
              ? `${prefix}${Math.round(proxy.v).toLocaleString('en-US')}`
              : `${prefix}${proxy.v.toFixed(decimals)}`;
          },
        },
        0.35 + i * 0.18,
      );
    });
    stampIn(tl, root.querySelectorAll('[data-cost-stamp]'), '-=0.3');
    return tl;
  });

  return (
    <section ref={rootRef} className="pt-16 md:pt-24">
      <div className={CONTAINER}>
        <DossierHead
          num="05"
          label="COST OF SCALE"
          title={data.cost.title}
          lead={data.cost.lead}
          leadClassName="max-w-[580px]"
        />
        <div className="mt-8 grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-px overflow-hidden rounded-ref-lg border border-line-2 bg-line-2 md:mt-10">
          <div
            data-cost-col
            className="flex flex-col gap-4 bg-surface px-6 py-[26px] md:px-[30px]"
          >
            <ColumnPill tone="neutral">{data.cost.ledgerPill}</ColumnPill>
            <div className="flex flex-col">
              {data.cost.ledger.map((row, i) => (
                <div
                  key={row.tier}
                  className={`flex flex-wrap items-baseline gap-3.5 py-3.5 ${
                    i < data.cost.ledger.length - 1 ? 'border-b border-line' : ''
                  }`}
                >
                  <span className="w-[82px] flex-none font-jetbrains text-[11px] tracking-[0.08em] text-dim">
                    {row.tier}
                  </span>
                  <span className="font-jetbrains text-[12.5px] text-muted">{row.math}</span>
                  <span className="ml-auto font-jetbrains text-[15px] font-semibold text-ink">
                    <span data-count={row.value}>{row.value}</span>
                    <span className="text-[10.5px] font-normal text-dim"> {row.unit}</span>
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-auto flex flex-wrap items-center gap-2.5 border-t border-line pt-3">
              <span className="font-jetbrains text-[10.5px] text-dim">{data.cost.ledgerFoot}</span>
              <span data-cost-stamp className="ml-auto">
                <Stamp tone="amber">{data.cost.ledgerStamp}</Stamp>
              </span>
            </div>
          </div>
          <div
            data-cost-col
            className="flex flex-col gap-4 bg-surface px-6 py-[26px] md:px-[30px]"
          >
            <ColumnPill tone="brand">{data.cost.riiPill}</ColumnPill>
            <div className="flex flex-1 flex-col justify-center gap-2.5 py-1.5">
              <span className="font-onest text-[clamp(26px,2.8vw,34px)] font-extrabold leading-[1.1] tracking-[-0.02em]">
                {data.cost.riiTitle}
                <span className="text-brand-600">.</span>
              </span>
              <p className="max-w-[420px] text-[14.5px] leading-[1.65] text-pretty text-muted">
                {data.cost.riiBody}
              </p>
            </div>
            <div className="mt-auto flex flex-wrap items-center gap-2.5 border-t border-line pt-3">
              <span className="font-jetbrains text-[10.5px] text-dim">{data.cost.riiFoot}</span>
              <span data-cost-stamp className="ml-auto">
                <Stamp tone="green">{data.cost.riiStamp}</Stamp>
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
